import React, { useState, useEffect } from 'react';
import { subdomainApi } from '../../services/subdomainApi';
import {
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Tabs,
  Tab,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  IconButton,
  Snackbar,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SecurityIcon from '@mui/icons-material/Security';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StopIcon from '@mui/icons-material/Stop';
import { useNavigate, useLocation } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudIcon from '@mui/icons-material/Cloud';
import RadarIcon from '@mui/icons-material/Radar';

// Aggiorniamo i colori del tema per matchare esattamente l'interfaccia
const THEME = {
  primary: '#64B5F6',
  secondary: '#B0BEC5',
  background: '#0A192F',
  surface: '#1A2C4C',
  border: '#2A3F63',
  error: '#FF4D4D',
  cardBg: 'rgba(26, 44, 76, 0.4)', // Per le card più scure
  success: '#4CAF50'
};

const SubdomainScanner = () => {
  const [domain, setDomain] = useState('');
  const [enableNmap, setEnableNmap] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [currentScanId, setCurrentScanId] = useState(null);
  const [scanResults, setScanResults] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [progress, setProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState('');
  const [domainDetails, setDomainDetails] = useState(null);
  const [nmapSummary, setNmapSummary] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domainNmapDetails, setDomainNmapDetails] = useState(null);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [scanHistory, setScanHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [domainsFound, setDomainsFound] = useState(0);
  const [nmapData, setNmapData] = useState(null);
  const [scanCredits, setScanCredits] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check for scan_id in URL when component mounts
    const params = new URLSearchParams(location.search);
    const urlScanId = params.get('uuid');
    if (urlScanId) {
      setCurrentScanId(urlScanId);
      loadExistingScan(urlScanId);
    }
  }, [location]);

  useEffect(() => {
    // Load scan history when component mounts
    loadScanHistory();
  }, []);

  useEffect(() => {
    if (currentScanId && !scanning && activeTab === 1) {
      subdomainApi.getNmapReport(currentScanId)
        .then(setNmapData)
        .catch(console.error);
    }
  }, [currentScanId, scanning, activeTab]);

  useEffect(() => {
    // Qui potresti fare una chiamata API per ottenere i crediti disponibili
    // Per ora impostiamo un valore di esempio
    setScanCredits(10);
  }, []);

  const loadScanHistory = async () => {
    try {
      setLoadingHistory(true);
      const history = await subdomainApi.getAllScans();
      setScanHistory(history);
    } catch (error) {
      console.error('Error loading scan history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadExistingScan = async (scanId) => {
    try {
      const results = await subdomainApi.getScanHistory(scanId);
      const status = await subdomainApi.getSubdomainScanStatus(scanId);

      if (status.is_running) {
        setScanning(true);
        startStatusPolling(scanId);
      } else {
        setScanResults(results);
      }
      
      setProgress(status.total_progress);
      setCurrentPhase(status.current_phase);
      setDomain(results.domain);
      setActiveTab(0); // Forza la visualizzazione della tab Subdomains
    } catch (error) {
      console.error('Error loading existing scan:', error);
    }
  };

  const handleStartScan = async () => {
    try {
      setScanning(true);
      setScanResults(null);
      setDomainDetails(null);
      const result = await subdomainApi.startSubdomainScan(domain, enableNmap);
      setCurrentScanId(result.scan_id);
      
      // Update URL with scan_id
      navigate(`/subdomains?uuid=${result.scan_id}`, { replace: true });
      
      startStatusPolling(result.scan_id);
    } catch (error) {
      console.error('Error starting scan:', error);
      setScanning(false);
    }
  };

  const startStatusPolling = (scanId) => {
    const pollInterval = setInterval(async () => {
      try {
        // Get status for progress and phase
        const status = await subdomainApi.getSubdomainScanStatus(scanId);
        setProgress(status.total_progress || 0);
        setCurrentPhase(status.current_phase || 'Inizializzazione');
        
        // Get live results during scan
        const liveResults = await subdomainApi.getLiveResults(scanId);
        if (liveResults) {
          setScanResults(liveResults);
          setDomainsFound(liveResults.total_domains || 0);
        }
        
        if (!status.is_running) {
          clearInterval(pollInterval);
          setScanning(false);
          // Get final results when scan completes
          const finalResults = await subdomainApi.getScanHistory(scanId);
          setScanResults(finalResults);
        }
      } catch (error) {
        console.error('Error during polling:', error);
        clearInterval(pollInterval);
        setScanning(false);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  };

  const handleStopScan = async () => {
    try {
      await subdomainApi.stopScan(currentScanId);
      setScanning(false);
      const finalResults = await subdomainApi.getScanHistory(currentScanId);
      setScanResults(finalResults);
    } catch (error) {
      console.error('Error stopping scan:', error);
    }
  };

  const handleCopyData = (text) => {
    navigator.clipboard.writeText(text);
    setCopySnackbar(true);
  };

  const renderDomainList = () => {
    if (!scanResults?.domains) return null;

    return (
      <Grid container spacing={3}>
        {Object.entries(scanResults.domains).map(([domainName, info]) => {
          if (!info || domainName.includes('\n')) return null;
          
          return (
            <Grid item xs={12} key={domainName}>
              <Paper 
                sx={{ 
                  p: 3,
                  backgroundColor: 'rgba(26, 44, 76, 0.6)',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${THEME.border}`,
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(100, 181, 246, 0.15)',
                    borderColor: THEME.primary
                  }
                }}
              >
                <Grid container spacing={3}>
                  {/* Domain Header */}
                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      mb: 2 
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SecurityIcon 
                          sx={{ 
                            color: THEME.primary,
                            mr: 2,
                            fontSize: 28 
                          }} 
                        />
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: THEME.primary,
                            fontFamily: 'monospace',
                            fontWeight: 'bold'
                          }}
                        >
                          {domainName}
                        </Typography>
                      </Box>
                      <Box>
                        <Tooltip title="Copy domain info">
                          <IconButton 
                            onClick={() => handleCopyData(JSON.stringify(info, null, 2))}
                            sx={{ 
                              color: THEME.secondary,
                              '&:hover': {
                                color: THEME.primary
                              }
                            }}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Sources & IPs */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          color: THEME.primary,
                          mb: 1,
                          fontWeight: 'bold'
                        }}
                      >
                        Sources
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 1 
                      }}>
                        {info.sources?.map(source => (
                          <Chip
                            key={source}
                            label={source}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(100, 181, 246, 0.1)',
                              color: THEME.secondary,
                              border: `1px solid ${THEME.border}`,
                              '&:hover': {
                                backgroundColor: 'rgba(100, 181, 246, 0.2)',
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        color: THEME.primary,
                        mb: 1,
                        fontWeight: 'bold'
                      }}
                    >
                      IPs
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 1 
                    }}>
                      {info.ips?.map(ip => (
                        <Chip
                          key={ip}
                          label={ip}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(100, 181, 246, 0.1)',
                            color: THEME.secondary,
                            border: `1px solid ${THEME.border}`,
                            fontFamily: 'monospace'
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>

                  {/* Cloud Services */}
                  {info.cloud_services?.length > 0 && (
                    <Grid item xs={12}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          color: THEME.primary,
                          mb: 1,
                          fontWeight: 'bold'
                        }}
                      >
                        Cloud Services
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 1 
                      }}>
                        {info.cloud_services.map(service => (
                          <Chip
                            key={service}
                            label={service}
                            size="small"
                            icon={<CloudIcon sx={{ color: THEME.primary }} />}
                            sx={{
                              backgroundColor: 'rgba(100, 181, 246, 0.1)',
                              color: THEME.secondary
                            }}
                          />
                        ))}
                      </Box>
                    </Grid>
                  )}

                  {/* DNS Info */}
                  {info.dns_info && (
                    <Grid item xs={12}>
                      <Accordion 
                        sx={{ 
                          backgroundColor: 'transparent',
                          border: `1px solid ${THEME.border}`,
                          '&:before': { display: 'none' }
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: THEME.primary }} />}>
                          <Typography sx={{ color: THEME.primary, fontWeight: 'bold' }}>
                            DNS Information
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            {Object.entries(info.dns_info).map(([type, records]) => {
                              if (!records?.length) return null;
                              return (
                                <Grid item xs={12} sm={6} key={type}>
                                  <Typography 
                                    variant="subtitle2" 
                                    sx={{ 
                                      color: THEME.primary,
                                      mb: 1 
                                    }}
                                  >
                                    {type}
                                  </Typography>
                                  {records.map(record => (
                                    <Typography 
                                      key={record} 
                                      variant="body2" 
                                      sx={{ 
                                        color: THEME.secondary,
                                        fontFamily: 'monospace' 
                                      }}
                                    >
                                      {record}
                                    </Typography>
                                  ))}
                                </Grid>
                              );
                            })}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  )}

                  {/* SSL Info */}
                  {info.ssl_info && (
                    <Grid item xs={12}>
                      <Paper 
                        sx={{ 
                          p: 2,
                          backgroundColor: 'rgba(100, 181, 246, 0.05)',
                          border: `1px solid ${THEME.border}`
                        }}
                      >
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            color: THEME.primary,
                            mb: 1,
                            fontWeight: 'bold'
                          }}
                        >
                          SSL Certificate
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography 
                              variant="body2" 
                              sx={{ color: THEME.secondary }}
                            >
                              Issuer: {info.ssl_info.issuer.organizationName}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ color: THEME.secondary }}
                            >
                              CN: {info.ssl_info.issuer.commonName}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography 
                              variant="body2" 
                              sx={{ color: THEME.secondary }}
                            >
                              Expires: {new Date(info.ssl_info.expires).toLocaleDateString()}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const handleExport = async () => {
    try {
      const exportData = await subdomainApi.exportScan(currentScanId);
      // Crea e scarica il file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scan-${currentScanId}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Errore durante l\'esportazione:', error);
    }
  };

  const handleDomainSelect = async (domain) => {
    try {
      setSelectedDomain(domain);
      const details = await subdomainApi.getNmapDomainDetails(currentScanId, domain);
      setDomainNmapDetails(details);
    } catch (error) {
      console.error('Errore nel caricamento dei dettagli Nmap:', error);
    }
  };

  const renderNetworkServices = () => {
    if (scanning && !nmapData) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          minHeight: '200px'
        }}>
          <CircularProgress sx={{ color: THEME.primary, mb: 2 }} />
          <Typography sx={{ color: THEME.secondary }}>
            Scansione della rete in corso...
          </Typography>
          <Typography variant="body2" sx={{ color: THEME.secondary, mt: 1, opacity: 0.8 }}>
            I risultati appariranno automaticamente al termine della scansione
          </Typography>
        </Box>
      );
    }

    if (!nmapData) return null;

    return (
      <Box>
        {/* Network Overview Card */}
        <Paper sx={{ 
          p: 3,
          mb: 3,
          backgroundColor: THEME.cardBg,
          borderRadius: 2,
        }}>
          <Typography variant="h6" sx={{ color: THEME.primary, mb: 3 }}>
            Network Overview
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 2, 
                backgroundColor: THEME.surface,
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" sx={{ color: THEME.secondary }}>
                  Open Ports
                </Typography>
                <Typography variant="h3" sx={{ color: THEME.primary }}>
                  {nmapData.summary.total_open_ports}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 2, 
                backgroundColor: THEME.surface,
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" sx={{ color: THEME.secondary }}>
                  Active Domains
                </Typography>
                <Typography variant="h3" sx={{ color: THEME.primary }}>
                  {nmapData.summary.domains_with_open_ports}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 2, 
                backgroundColor: THEME.surface,
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" sx={{ color: THEME.secondary }}>
                  Common Services
                </Typography>
                <Typography variant="h3" sx={{ color: THEME.primary }}>
                  {Object.keys(nmapData.summary.common_services || {}).length}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Domain Services Cards */}
        {Object.entries(nmapData.domains || {}).map(([domain, info]) => (
          <Paper 
            key={domain}
            sx={{ 
              p: 3,
              mb: 2,
              backgroundColor: THEME.cardBg,
              borderRadius: 2,
              border: `1px solid ${THEME.border}`
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mb: 2
            }}>
              <SecurityIcon sx={{ 
                color: THEME.primary, 
                mr: 2,
                fontSize: 20 
              }} />
              <Typography 
                sx={{ 
                  color: THEME.primary,
                  fontFamily: 'monospace',
                  fontSize: '1rem'
                }}
              >
                {domain}
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              {Object.entries(info.ports || {}).map(([port, details]) => (
                <Grid item xs={12} md={4} key={port}>
                  <Box sx={{ 
                    p: 2,
                    backgroundColor: THEME.surface,
                    borderRadius: 1,
                    border: `1px solid ${THEME.border}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}>
                    <Box sx={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <Typography 
                        sx={{ 
                          color: THEME.primary,
                          fontFamily: 'monospace',
                          fontSize: '0.9rem'
                        }}
                      >
                        Port {port}/tcp
                      </Typography>
                      <Chip
                        label={details.state}
                        size="small"
                        sx={{
                          height: '20px',
                          backgroundColor: 'transparent',
                          border: `1px solid ${details.state === 'open' ? '#4CAF50' : THEME.error}`,
                          color: details.state === 'open' ? '#4CAF50' : THEME.error,
                          '& .MuiChip-label': {
                            px: 1,
                            fontSize: '0.7rem'
                          }
                        }}
                      />
                    </Box>
                    <Typography 
                      sx={{ 
                        color: THEME.secondary,
                        fontSize: '0.85rem',
                        opacity: 0.8
                      }}
                    >
                      {details.service}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        ))}
      </Box>
    );
  };

  const renderNmapSummary = () => {
    if (!nmapSummary) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography sx={{ color: THEME.secondary }}>
            Click "Nmap Analysis" to view the summary
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ 
            p: 3,
            backgroundColor: 'rgba(26, 44, 76, 0.6)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${THEME.border}`,
            borderRadius: 2
          }}>
            <Typography variant="h6" sx={{ color: THEME.primary, mb: 3 }}>Scan Statistics</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(100, 181, 246, 0.1)',
                  borderRadius: 1,
                  border: `1px solid ${THEME.border}`,
                  textAlign: 'center'
                }}>
                  <Typography variant="subtitle2" sx={{ color: THEME.secondary }}>Total Domains</Typography>
                  <Typography variant="h4" sx={{ color: THEME.primary }}>
                    {nmapSummary.summary.total_domains}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(100, 181, 246, 0.1)',
                  borderRadius: 1,
                  border: `1px solid ${THEME.border}`,
                  textAlign: 'center'
                }}>
                  <Typography variant="subtitle2" sx={{ color: THEME.secondary }}>Active Domains</Typography>
                  <Typography variant="h4" sx={{ color: THEME.primary }}>
                    {nmapSummary.summary.domains_with_open_ports}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(100, 181, 246, 0.1)',
                  borderRadius: 1,
                  border: `1px solid ${THEME.border}`,
                  textAlign: 'center'
                }}>
                  <Typography variant="subtitle2" sx={{ color: THEME.secondary }}>Total Open Ports</Typography>
                  <Typography variant="h4" sx={{ color: THEME.primary }}>
                    {nmapSummary.summary.total_open_ports}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Common Services */}
        <Grid item xs={12}>
          <Paper sx={{ 
            p: 3,
            backgroundColor: 'rgba(26, 44, 76, 0.6)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${THEME.border}`,
            borderRadius: 2
          }}>
            <Typography variant="h6" sx={{ color: THEME.primary, mb: 3 }}>Common Services</Typography>
            <Grid container spacing={2}>
              {Object.entries(nmapSummary.summary.common_services || {}).map(([service, count]) => (
                <Grid item xs={12} sm={6} md={4} key={service}>
                  <Box sx={{ 
                    p: 2,
                    backgroundColor: 'rgba(100, 181, 246, 0.05)',
                    border: `1px solid ${THEME.border}`,
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <Typography sx={{ color: THEME.secondary }}>{service}</Typography>
                    <Chip 
                      label={count}
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(100, 181, 246, 0.1)',
                        color: THEME.primary,
                        border: `1px solid ${THEME.border}`
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderScanHistory = () => (
    <Paper sx={{ 
      p: 3, 
      backgroundColor: THEME.cardBg,
      borderRadius: 2
    }}>
      <Typography 
        variant="h6" 
        sx={{ 
          color: THEME.primary,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <SecurityIcon /> Recent Scans
      </Typography>
      {loadingHistory ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} sx={{ color: THEME.primary }} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {scanHistory.map((scan) => (
            <Grid item xs={12} key={scan.scan_id}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: THEME.surface,
                  borderRadius: 1,
                  border: `1px solid ${THEME.border}`,
                  '&:hover': {
                    borderColor: THEME.primary,
                    backgroundColor: 'rgba(26, 44, 76, 0.8)'
                  }
                }}
              >
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography sx={{ color: THEME.primary, fontFamily: 'monospace' }}>
                      {scan.domain}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="body2" sx={{ color: THEME.secondary }}>
                        Status: {scan.status}
                      </Typography>
                      <Typography variant="body2" sx={{ color: THEME.secondary }}>
                        Started: {new Date(scan.start_time).toLocaleString()}
                      </Typography>
                      {scan.end_time && (
                        <Typography variant="body2" sx={{ color: THEME.secondary }}>
                          Completed: {new Date(scan.end_time).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/subdomains?uuid=${scan.scan_id}`);
                        setCopySnackbar(true);
                      }}
                      sx={{
                        mr: 1,
                        borderColor: THEME.border,
                        color: THEME.secondary,
                        '&:hover': {
                          borderColor: THEME.primary,
                          color: THEME.primary
                        }
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16, mr: 1 }} />
                      Copy Link
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={async () => {
                        await loadExistingScan(scan.scan_id);
                        navigate(`/subdomains?uuid=${scan.scan_id}`, { replace: true });
                      }}
                      sx={{
                        backgroundColor: THEME.surface,
                        color: THEME.primary,
                        '&:hover': {
                          backgroundColor: 'rgba(100, 181, 246, 0.1)'
                        }
                      }}
                    >
                      View Scan
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: THEME.background,
      p: 3
    }}>
      {/* Header */}
      <Paper sx={{ 
        p: 3,
        mb: 3,
        backgroundColor: THEME.cardBg,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <SecurityIcon sx={{ 
          fontSize: 40,
          color: THEME.primary 
        }} />
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              color: THEME.primary,
              fontWeight: 'normal'
            }}
          >
            SubRecon Security Scanner
          </Typography>
          <Typography 
            variant="subtitle2"
            sx={{ color: THEME.secondary }}
          >
            Comprehensive Subdomain & Port Scanner
          </Typography>
        </Box>
      </Paper>

      {/* Search Bar Section */}
      <Box sx={{ 
        display: 'flex',
        gap: 2,
        mb: 3
      }}>
        <Box sx={{ flex: 1, display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Enter target domain (e.g. example.com)"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            disabled={scanning}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: THEME.surface,
                borderRadius: 1,
                color: '#fff',
                '& fieldset': {
                  borderColor: 'transparent'
                },
                '&:hover fieldset': {
                  borderColor: THEME.primary
                }
              }
            }}
          />
          <Paper 
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              backgroundColor: THEME.surface,
              border: `1px solid ${enableNmap ? THEME.primary : 'transparent'}`,
              borderRadius: 1,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: THEME.primary,
                backgroundColor: 'rgba(26, 44, 76, 0.8)'
              }
            }}
            onClick={() => setEnableNmap(!enableNmap)}
          >
            <RadarIcon 
              sx={{ 
                color: enableNmap ? THEME.primary : THEME.secondary,
                transition: 'color 0.2s ease'
              }} 
            />
            <Typography 
              sx={{ 
                color: enableNmap ? THEME.primary : THEME.secondary,
                fontSize: '0.9rem',
                transition: 'color 0.2s ease'
              }}
            >
              Port Scan
            </Typography>
            <Switch
              size="small"
              checked={enableNmap}
              onChange={(e) => setEnableNmap(e.target.checked)}
              disabled={scanning}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: THEME.primary
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: THEME.primary
                }
              }}
            />
          </Paper>
        </Box>
        <Button
          variant="contained"
          onClick={handleStartScan}
          disabled={scanning || !domain}
          sx={{
            backgroundColor: '#0288d1',
            minWidth: '120px',
            '&:hover': {
              backgroundColor: '#0277bd'
            }
          }}
        >
          START SCAN
        </Button>
      </Box>

      {/* Progress Section con stile migliorato */}
      {scanning && (
        <Paper sx={{ 
          p: 3, 
          mb: 3,
          backgroundColor: 'rgba(26, 44, 76, 0.6)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${THEME.border}`,
          borderRadius: 2
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: THEME.primary }}>
              Progress: {Math.round(progress)}%
            </Typography>
            <Button
              startIcon={<StopIcon />}
              variant="outlined"
              color="error"
              onClick={handleStopScan}
              sx={{
                borderColor: THEME.error,
                color: THEME.error,
                '&:hover': {
                  backgroundColor: 'rgba(255, 77, 77, 0.1)',
                  borderColor: THEME.error
                }
              }}
            >
              Stop Scan
            </Button>
          </Box>
          <Typography variant="body2" sx={{ color: THEME.secondary }} gutterBottom>
            {currentPhase || 'Initializing scan...'}
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={progress}
            sx={{
              backgroundColor: 'rgba(176, 190, 197, 0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: THEME.primary
              }
            }}
          />
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: THEME.secondary }}>
              Subdomains found: {scanResults?.total_domains || 0}
            </Typography>
          </Box>
        </Paper>
      )}

      {scanResults && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Subdomains" />
              <Tab label="Network Services" />
              <Tab label="Nmap Summary" />
              <Tab label="Statistics" />
              <Tab label="History" />
            </Tabs>
            <Box>
              <Button
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                sx={{ mr: 1 }}
              >
                Esporta
              </Button>
              {!scanning && (
                <Button
                  startIcon={<SecurityIcon />}
                  onClick={() => subdomainApi.getNmapSummary(currentScanId).then(setNmapSummary)}
                >
                  Analisi Nmap
                </Button>
              )}
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            {activeTab === 0 && renderDomainList()}
            {activeTab === 1 && renderNetworkServices()}
            {activeTab === 2 && renderNmapSummary()}
            {activeTab === 3 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">Statistiche Generali</Typography>
                  <Typography>Totale domini trovati: {scanResults.total_domains}</Typography>
                  <Typography>
                    Tempo di scansione: {Math.round(scanResults.timing?.elapsed || 0)}s
                  </Typography>
                  <Typography>
                    Fase corrente: {currentPhase || 'Completato'}
                  </Typography>
                </Grid>
              </Grid>
            )}
            {activeTab === 4 && renderScanHistory()}
          </Box>
        </Paper>
      )}

      {/* Dialog per i dettagli Nmap del dominio */}
      <Dialog
        open={Boolean(selectedDomain)}
        onClose={() => setSelectedDomain(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Dettagli Nmap: {selectedDomain}
        </DialogTitle>
        <DialogContent>
          {domainNmapDetails && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">IPs:</Typography>
                <Typography>{domainNmapDetails.ips.join(', ')}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Porte:</Typography>
                {Object.entries(domainNmapDetails.ports).map(([port, info]) => (
                  <Box key={port} sx={{ mb: 1 }}>
                    <Typography>
                      {port} - {info.state} - {info.service}
                      {info.details.length > 0 && ` (${info.details.join(', ')})`}
                    </Typography>
                  </Box>
                ))}
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message="Copied to clipboard"
      />
    </Box>
  );
};

export default SubdomainScanner;
